import { FTP_ACCOUNT, type FtpAccount, type ManualUploadResult } from '@flux/shared';
import { useMutation } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { cn } from '@/lib/cn.js';
import { toast } from '@/lib/toast.js';
import { fileToBase64, uploadManualCsv } from './api.js';

export interface UploadCsvDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploaded: () => void;
  disabled?: boolean;
}

function CheckRow({ label, ok }: { label: string; ok: boolean }): JSX.Element {
  return (
    <li className="flex items-center gap-2">
      <span
        className={cn(
          'w-4 text-center font-semibold',
          ok ? 'text-status-green-fg' : 'text-status-red-fg',
        )}
      >
        {ok ? '✓' : '✗'}
      </span>{' '}
      <span className={ok ? 'text-foreground' : 'text-status-red-fg'}>{label}</span>
    </li>
  );
}

/**
 * « Uploader un CSV » (`01` §4, `02` §5). L'API `/api/files/upload` réalise le
 * contrôle de validité *et* l'envoi en un seul appel (`uploaded: false`
 * uniquement quand un contrôle bloquant a refusé le dépôt) : il n'existe pas
 * d'appel « vérifier seulement » côté serveur. Le bouton est donc libellé
 * « Vérifier et envoyer » plutôt que de mimer une confirmation en deux temps
 * qui n'existe pas réellement (voir le rapport de la vague).
 */
export function UploadCsvDialog({
  open,
  onOpenChange,
  onUploaded,
  disabled = false,
}: UploadCsvDialogProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [ftpAccount, setFtpAccount] = useState<FtpAccount>('215');
  const [result, setResult] = useState<ManualUploadResult | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error('Sélectionnez un fichier.');
      }
      const contentBase64 = await fileToBase64(file);
      return uploadManualCsv({ fileName: file.name, ftpAccount, contentBase64 });
    },
    onSuccess: (data) => {
      setResult(data);
      if (data.uploaded) {
        toast.success(`Fichier « ${data.fileName} » envoyé.`);
        onUploaded();
      }
    },
    onError: () => {
      toast.error('Une erreur technique est survenue. Réessayez.');
    },
  });

  function reset(): void {
    setFile(null);
    setFtpAccount('215');
    setResult(null);
    mutation.reset();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Uploader un CSV</DialogTitle>
          <DialogDescription>
            Le contrôle de validité et l’envoi au FTP Martelet sont effectués en une seule étape.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <Field label="Fichier CSV" htmlFor="upload-file">
            <Input
              id="upload-file"
              type="file"
              accept=".csv"
              disabled={disabled}
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setResult(null);
              }}
            />
          </Field>

          <Field label="Compte FTP" htmlFor="upload-account">
            <Select
              value={ftpAccount}
              onValueChange={(next) => {
                setFtpAccount(next as FtpAccount);
              }}
            >
              <SelectTrigger id="upload-account" disabled={disabled}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(FTP_ACCOUNT).map((entry) => (
                  <SelectItem key={entry.code} value={entry.code}>
                    {entry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {disabled ? (
            <p
              role="alert"
              className="rounded-md border border-status-orange-border bg-status-orange-bg px-3 py-2 text-sm text-status-orange-fg"
            >
              L’upload manuel est désactivé pendant le mode maintenance.
            </p>
          ) : null}

          {result ? (
            <div className="flex flex-col gap-3 rounded-md bg-muted/60 p-3 text-sm">
              <div className="flex flex-col gap-0.5">
                <p className="break-words font-medium">
                  {result.uploaded ? `Envoyé sous le nom « ${result.fileName} ».` : 'Envoi refusé.'}
                </p>
                <p className="break-all text-xs text-muted-foreground">
                  Première ligne : {result.check.firstLine || '(vide)'}
                </p>
                <p className="text-xs tabular-nums text-muted-foreground">
                  {result.check.lineCount} ligne(s), {result.check.headerCount} colonne(s)
                  d’en-tête.
                </p>
              </div>
              <ul className="flex flex-col gap-1">
                <CheckRow label="Extension .csv" ok={result.check.extensionValid} />
                <CheckRow label="Première ligne" ok={result.check.firstLineValid} />
                <CheckRow label="Séparateur" ok={result.check.delimiterValid} />
                <CheckRow label="Encodage" ok={result.check.encodingValid} />
                <CheckRow label="Nom de fichier" ok={result.check.nameValid} />
              </ul>
              {result.check.suggestedName ? (
                <p className="break-all text-status-blue-fg">
                  Nom recommandé : {result.check.suggestedName}
                </p>
              ) : null}
              {result.check.errors.length > 0 ? (
                <ul className="flex list-disc flex-col gap-0.5 border-t border-border pl-4 pt-2 text-status-red-fg">
                  {result.check.errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Fermer
          </Button>
          <Button
            type="button"
            onClick={() => {
              mutation.mutate();
            }}
            disabled={!file || disabled}
            isLoading={mutation.isPending}
          >
            Vérifier et envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
